import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  type CompetitionRules,
  type LeaderboardRow,
  type ResultsGrid,
  type Round,
  type SelectableTeam,
} from "../lib/api";
import { analytics, AnalyticsEvent } from "../lib/analytics";
import { trackApiError } from "../lib/analytics/errorTracking";
import { LazyPromoSlot } from "../lib/promo/LazyPromoSlot";
import { SettingsPanel } from "./competition/SettingsPanel";
import { ResultsGridPanel } from "./competition/ResultsGridPanel";
import { useCompetitionLive } from "../lib/live";
import {
  Avatar,
  Badge,
  Button,
  Countdown,
  Crest,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  InfoNote,
  ProgressBar,
  SegmentedControl,
  Skeleton,
  Tabs,
  cx,
  formatKickoff,
  formatMatchDay,
  formatTime,
  useCopy,
  useToast,
  type IconName,
} from "../components/ui";

const COMPETITION_AD_SLOT = import.meta.env.VITE_ADSENSE_SLOT_COMPETITION ?? "";

type TabValue = "pick" | "players" | "grid" | "rounds" | "rules" | "settings";

/**
 * How often the page re-fetches on its own, ignoring the live signal.
 *
 * Deliberately slow. It exists so the screen still converges if the live endpoint is
 * unreachable — an older API, a blocked request — rather than because anything here is
 * expected to depend on it.
 */
const BACKSTOP_REFETCH_MS = 120_000;

/**
 * The competition screen. Three things matter, in this order: how long until the deadline,
 * which team you're picking, and who else is still alive. Everything else is secondary.
 */
export function CompetitionPage() {
  const { id = "" } = useParams();
  const [tab, setTab] = useState<TabValue>("pick");

  const { data: competition, isLoading, error: competitionError, refetch } = useQuery({
    queryKey: ["competition", id],
    queryFn: () => api.competitions.get(id),
    /**
     * A backstop, not the mechanism. `useCompetitionLive` below invalidates this the moment
     * the server says anything changed, which is what makes a settled result appear straight
     * away; this interval only covers the case where that polling can't run at all.
     */
    refetchInterval: BACKSTOP_REFETCH_MS,
  });

  /**
   * One poll for the whole screen.
   *
   * Every query on this page — the grid, the leaderboard, the round's picks and fixtures — is
   * a plain `useQuery` with no interval of its own. This hook watches a single cheap endpoint
   * and invalidates the lot when, and only when, the server's version changes, so a result
   * landing updates the grid within seconds without anything polling the grid itself.
   */
  const live = useCompetitionLive(id);

  useEffect(() => {
    if (competitionError) trackApiError(competitionError, "view_competition");
  }, [competitionError]);

  useEffect(() => {
    if (competition) {
      analytics.track(AnalyticsEvent.CompetitionViewed, {
        competitionId: competition.id,
        role: competition.role,
        status: competition.status,
      });
    }
    // Fires once per competition id, not on every 60s background refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition?.id]);

  function changeTab(next: TabValue) {
    analytics.track(AnalyticsEvent.CompetitionTabChanged, { tab: next });
    setTab(next);
  }

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-12 w-72" />
        <Skeleton className="h-28" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (competitionError) {
    return (
      <div className="card mx-auto max-w-xl p-6 text-center sm:p-8">
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-danger-soft text-danger-text">
          <Icon name="refresh" className="size-6" />
        </span>
        <h1 className="display-title mt-4 text-2xl text-fg">Couldn't load this competition</h1>
        <p className="mt-2 text-sm leading-relaxed text-fg-subtle">
          {competitionError instanceof ApiError
            ? competitionError.message
            : "Check your connection and try again."}
        </p>
        <Button className="mt-5" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!competition) return <EmptyState title="Competition not found" />;

  const entry = competition.yourEntry;
  const round = competition.currentRound;

  const tabs: Array<{ value: TabValue; label: string; icon: IconName }> = [
    { value: "pick", label: "Your pick", icon: "ball" },
    { value: "players", label: "Players", icon: "users" },
    { value: "grid", label: "Grid", icon: "grid" },
    { value: "rounds", label: "Rounds", icon: "calendar" },
    { value: "rules", label: "Rules", icon: "shield" },
    ...(competition.role === "admin"
      ? [{ value: "settings" as const, label: "Settings", icon: "settings" as const }]
      : []),
  ];

  return (
    <>
      <header className="mb-6">
        <p className="label mb-2">
          {competition.league.name} · {competition.season.label}
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="display-title text-[2.5rem] text-fg sm:text-5xl">{competition.name}</h1>
          <Badge tone={competition.status === "running" ? "alive" : "neutral"}>
            {competition.status}
          </Badge>
        </div>
        {competition.description && (
          <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-fg-subtle">
            {competition.description}
          </p>
        )}
      </header>

      {competition.winner && (
        <WinnerBanner names={[competition.winner.displayName]} label="Last man standing" />
      )}
      {competition.sharedWinners.length > 0 && (
        <WinnerBanner
          names={competition.sharedWinners.map((w) => w.displayName)}
          label="Shared result"
          shared
        />
      )}

      <StatusStrip
        round={round}
        entryStatus={entry?.status ?? null}
        livesRemaining={entry?.livesRemaining ?? 0}
        eliminatedRound={entry?.eliminatedRound ?? null}
        playersAlive={competition.playersAlive}
        playersTotal={competition.playersTotal}
      />

      {competition.role === "admin" && competition.inviteCode && (
        <InviteCodeCard
            code={competition.inviteCode}
            joinable={competition.isJoinable}
            competitionName={competition.name}
          />
      )}

      <div className="under-header mb-6 bg-canvas/95 backdrop-blur-sm">
        <Tabs value={tab} onChange={changeTab} tabs={tabs} />
      </div>

      <div key={tab} className="animate-fade">
        {tab === "pick" &&
          (round ? (
            <PickPanel
              competitionId={id}
              round={round}
              canPick={entry?.status === "alive" && competition.status === "running"}
              eliminated={entry?.status === "out"}
              notEntered={!entry}
              rules={competition.rules}
            />
          ) : (
            <EmptyState
              icon={<Icon name="calendar" className="size-7" />}
              title="No rounds yet"
              description="Fixtures haven't been imported for this league yet. Once they are, round 1 opens automatically."
            />
          ))}

        {tab === "players" && <PlayersPanel competitionId={id} />}
        {tab === "grid" && <ResultsGridPanel competition={competition} live={live} />}
        {tab === "rounds" && <RoundsPanel competitionId={id} />}
        {tab === "rules" && (
          <RulesPanel summary={competition.ruleSummary} locked={competition.rulesLocked} />
        )}
        {tab === "settings" && competition.role === "admin" && (
          <SettingsPanel competition={competition} />
        )}
      </div>

      {/* Never on the pick tab — nothing competes with the deadline and the team grid. */}
      {tab !== "pick" && COMPETITION_AD_SLOT && (
        <LazyPromoSlot slotId={COMPETITION_AD_SLOT} className="mt-10" />
      )}
    </>
  );
}

/* Header pieces ------------------------------------------------------------ */

function WinnerBanner({
  names,
  label,
  shared,
}: {
  names: string[];
  label: string;
  shared?: boolean;
}) {
  return (
    <div
      className={cx(
        "animate-pop mb-5 flex items-center gap-4 rounded-card border p-5",
        shared ? "border-warn-line bg-warn-soft" : "border-accent-line bg-accent-soft",
      )}
    >
      <span
        className={cx(
          // Amber is a *light* fill in dark mode, so its foreground has to be the near-black
          // contrast token rather than white — white-on-amber is unreadable there.
          "grid size-12 shrink-0 place-items-center rounded-2xl",
          shared ? "bg-warn text-contrast" : "bg-accent text-accent-fg",
        )}
      >
        <Icon name="trophy" className="size-7" />
      </span>
      <div className="min-w-0">
        <p className={cx("label", shared ? "text-warn-text" : "text-accent-text")}>{label}</p>
        <p className="mt-0.5 truncate text-xl font-bold text-fg">{names.join(", ")}</p>
      </div>
    </div>
  );
}

/** The code is the only way in, so admins need it close to hand. */
function InviteCodeCard({
  code,
  joinable,
  competitionName,
}: {
  code: string;
  joinable: boolean;
  competitionName: string;
}) {
  const { copied, copy } = useCopy();
  const toast = useToast();
  const link = `${window.location.origin}/join/${code}`;

  /**
   * Native share sheet where there is one, clipboard everywhere else.
   *
   * `navigator.share` must be called straight out of the click to count as a user gesture, so
   * the fallback is chosen by feature detection rather than by trying and catching. An abort
   * (the sheet dismissed) is a normal outcome, not a failure worth reporting.
   */
  async function shareLink() {
    const text = `Join my Last Man Standing competition, ${competitionName}`;

    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: competitionName, text, url: link });
        return;
      } catch (error) {
        if ((error as { name?: string }).name === "AbortError") return;
      }
    }

    toast.show((await copy(link)) ? "Invite link copied" : "Couldn't copy the link");
  }

  return (
    <div className="card mb-6 flex flex-wrap items-center gap-4 p-5">
      <div className="min-w-0">
        <p className="label mb-1.5">Invite code</p>
        <code className="tabular text-2xl font-bold tracking-[0.22em] text-accent-text">{code}</code>
      </div>
      <p className="order-last w-full text-sm leading-relaxed text-fg-subtle sm:order-none sm:w-auto sm:min-w-[12rem] sm:flex-1">
        {joinable
          ? "Send the link and they'll join in one tap — or read them the code."
          : "New entries are closed — reopen them in Settings to let anyone else in."}
      </p>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={async () => {
            if (await copy(code)) toast.show("Invite code copied");
          }}
        >
          <Icon name={copied ? "check" : "copy"} className="size-4" />
          {copied ? "Copied" : "Code"}
        </Button>
        <Button onClick={() => void shareLink()}>
          <Icon name="share" className="size-4" />
          Share link
        </Button>
      </div>
    </div>
  );
}

/** The deadline is the loudest element on the page — everything else can wait a minute. */
function StatusStrip({
  round,
  entryStatus,
  livesRemaining,
  eliminatedRound,
  playersAlive,
  playersTotal,
}: {
  round: Round | null;
  entryStatus: "alive" | "out" | null;
  livesRemaining: number;
  eliminatedRound: number | null;
  playersAlive: number;
  playersTotal: number;
}) {
  if (!round) return null;
  const locked = round.status !== "open";

  return (
    // `gap-px` over a line-coloured background draws the separators, rather than `divide-x`,
    // which would put a stray left border on the first cell of every wrapped row.
    <div className="card mb-6 overflow-hidden p-0">
      <div className="grid grid-cols-2 gap-px bg-line sm:grid-cols-4">
        <div className="bg-surface p-4 sm:p-5">
          <p className="label mb-1.5">Round</p>
          <p className="tabular font-display text-3xl font-bold leading-none text-fg sm:text-4xl">
            {round.roundNumber}
          </p>
          <p className="mt-1.5 text-[12px] text-fg-subtle">{round.fixtureCount} fixtures</p>
        </div>

        <div className="bg-surface p-4 sm:p-5">
          <p className="label mb-1.5">{locked ? "Status" : "Deadline"}</p>
          {locked ? (
            <p className="font-display text-3xl font-bold capitalize leading-none text-fg sm:text-4xl">
              {round.status}
            </p>
          ) : (
            <Countdown to={round.deadlineAt} variant="blocks" />
          )}
          <p className="mt-1.5 truncate text-[12px] text-fg-subtle">
            {formatKickoff(round.deadlineAt)}
          </p>
        </div>

        <div className="bg-surface p-4 sm:p-5">
          <p className="label mb-1.5">You</p>
          {entryStatus === "alive" ? (
            <>
              <p className="font-display text-3xl font-bold leading-none text-accent-text sm:text-4xl">
                Alive
              </p>
              <p className="mt-1.5 text-[12px] text-fg-subtle">
                {livesRemaining > 0
                  ? `${livesRemaining} ${livesRemaining === 1 ? "life" : "lives"} left`
                  : "Sudden death"}
              </p>
            </>
          ) : entryStatus === "out" ? (
            <>
              <p className="font-display text-3xl font-bold leading-none text-danger-text sm:text-4xl">
                Out
              </p>
              <p className="mt-1.5 text-[12px] text-fg-subtle">
                {eliminatedRound ? `Eliminated in round ${eliminatedRound}` : "Eliminated"}
              </p>
            </>
          ) : (
            <>
              <p className="font-display text-2xl font-bold leading-none text-fg-subtle sm:text-3xl">
                Not entered
              </p>
              <p className="mt-1.5 text-[12px] text-fg-subtle">Watching only</p>
            </>
          )}
        </div>

        <div className="bg-surface p-4 sm:p-5">
          <p className="label mb-1.5">Standing</p>
          <p className="tabular font-display text-3xl font-bold leading-none text-fg sm:text-4xl">
            {playersAlive}
            <span className="text-lg text-fg-faint">/{playersTotal}</span>
          </p>
          <ProgressBar className="mt-2.5" value={playersAlive} max={playersTotal} />
        </div>
      </div>
    </div>
  );
}

/* Pick --------------------------------------------------------------------- */

type PickView = "fixtures" | "teams";

const PICK_VIEW_KEY = "lastman:pickView";

const PICK_VIEW_OPTIONS: Array<{ value: PickView; label: string; icon: IconName }> = [
  { value: "fixtures", label: "Fixtures", icon: "list" },
  { value: "teams", label: "Teams", icon: "grid" },
];

/**
 * Which layout the player prefers, remembered across visits.
 *
 * Fixtures is the default because the decision is "who wins this match", and seeing both sides
 * together is what that question needs. But the flat team list is genuinely better for one
 * thing — scanning alphabetically for a specific club when you already know who you want — so
 * it stays available rather than being replaced outright.
 */
function usePickView(): [PickView, (next: PickView) => void] {
  const [view, setView] = useState<PickView>(() => {
    try {
      return localStorage.getItem(PICK_VIEW_KEY) === "teams" ? "teams" : "fixtures";
    } catch {
      // Private browsing can throw on access; the default is fine for this session.
      return "fixtures";
    }
  });

  const choose = (next: PickView) => {
    setView(next);
    try {
      localStorage.setItem(PICK_VIEW_KEY, next);
    } catch {
      // Not persisting is survivable — the choice still holds for this session.
    }
  };

  return [view, choose];
}

function PickPanel({
  competitionId,
  round,
  canPick,
  eliminated,
  notEntered,
  rules,
}: {
  competitionId: string;
  round: Round;
  canPick: boolean;
  eliminated: boolean;
  notEntered: boolean;
  rules: CompetitionRules;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [view, setView] = usePickView();
  const [deadlineReached, setDeadlineReached] = useState(
    () => Date.now() >= new Date(round.deadlineAt).getTime(),
  );

  useEffect(() => {
    const remaining = new Date(round.deadlineAt).getTime() - Date.now();
    setDeadlineReached(remaining <= 0);
    if (remaining <= 0) return;
    // Flip the UI to locked the instant the deadline passes, without waiting for a refetch.
    const timer = window.setTimeout(() => setDeadlineReached(true), remaining + 50);
    return () => window.clearTimeout(timer);
  }, [round.deadlineAt]);

  const picksOpen = canPick && round.status === "open" && !deadlineReached;

  const { data: teams, isLoading } = useQuery({
    queryKey: ["teams", competitionId, round.id],
    queryFn: () => api.competitions.teams(competitionId, round.id),
    enabled: !notEntered,
  });

  /**
   * Once the deadline has gone, the two queries below stop being static.
   *
   * Scores land as matches finish, and so do outcomes — a player is decided at their own final
   * whistle, not when the round closes. Both are invalidated by `useCompetitionLive` on the
   * page above as soon as the server reports a change, so what's left here is only the
   * backstop for when that signal isn't available. Before the deadline there is nothing to
   * chase at all: the fixture list is settled and every score is empty.
   */
  const roundInPlay = round.status === "locked" || deadlineReached;

  // The round's fixtures, so the board can be laid out as matches rather than as a flat list
  // of teams. Both endpoints already exist and are fetched in parallel; the join is the only
  // new work, and it belongs here because it's presentation, not a rule.
  const { data: fixtures, isLoading: fixturesLoading } = useQuery({
    queryKey: ["fixtures", competitionId, round.id],
    queryFn: () => api.competitions.fixtures(competitionId, round.id),
    enabled: !notEntered,
    ...(roundInPlay ? { refetchInterval: BACKSTOP_REFETCH_MS } : {}),
  });

  const { data: picks } = useQuery({
    queryKey: ["picks", competitionId, round.id],
    queryFn: () => api.competitions.picks(competitionId, round.id),
    ...(roundInPlay ? { refetchInterval: BACKSTOP_REFETCH_MS } : {}),
  });

  const myPick = picks?.find((p) => p.isSelf);

  const mutation = useMutation({
    mutationFn: (teamId: string) => api.competitions.pick(competitionId, round.id, teamId),
    onSuccess: (_result, teamId) => {
      analytics.track(AnalyticsEvent.PickSubmitted, { competitionId, roundId: round.id });
      const team = teams?.find((t) => t.teamId === teamId);
      toast.show(team ? `${team.name} locked in` : "Pick saved");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["picks", competitionId, round.id] });
      queryClient.invalidateQueries({ queryKey: ["teams", competitionId, round.id] });
      queryClient.invalidateQueries({ queryKey: ["competition", competitionId] });
      queryClient.invalidateQueries({ queryKey: ["results-grid", competitionId] });
    },
    onError: (err) => {
      trackApiError(err, "submit_pick");
      setError(err instanceof ApiError ? err.message : "Couldn't save that pick.");
    },
  });

  /**
   * The board: one row per fixture, in kick-off order, each side a pick target.
   *
   * `selectableTeams` returns every team in the round exactly once — a team playing twice is
   * listed against its *first* fixture, which is the one settlement uses. So a lookup by team
   * id resolves every side of every fixture, and a team that appears in two rows resolves to
   * the same pick either way. That's why picking them from the later row is still correct.
   */
  const board = useMemo<FixtureRow[]>(() => {
    if (!fixtures?.length) return [];
    const byTeam = new Map((teams ?? []).map((team) => [team.teamId, team]));
    const query = search.trim().toLowerCase();

    return [...fixtures]
      // Kick-off order, stated here rather than inherited: a fixture list that isn't
      // chronological stops matching how anyone thinks about a gameweek.
      .sort((a, b) => new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime())
      .filter(
        (fixture) =>
          !query ||
          fixture.homeTeam.name.toLowerCase().includes(query) ||
          fixture.awayTeam.name.toLowerCase().includes(query),
      )
      .map((fixture) => {
        const side = (team: { id: string; name: string; crestUrl: string | null }): FixtureSide => {
          const selectable = byTeam.get(team.id);
          return {
            teamId: team.id,
            name: team.name,
            // Falls back to the full name only if the team somehow isn't selectable at all,
            // which would also mean it can't be picked — so the label never goes missing.
            shortName: selectable?.shortName ?? team.name,
            crestUrl: team.crestUrl,
            used: selectable?.used ?? true,
            playsTwice: selectable?.playsTwice ?? false,
            selectable: selectable != null,
          };
        };

        const home = side(fixture.homeTeam);
        const away = side(fixture.awayTeam);
        return {
          fixtureId: fixture.id,
          kickoffAt: fixture.kickoffAt,
          home,
          away,
          // Both sides gone means the fixture is still worth showing — it's part of the round,
          // and its absence would read as a missing match rather than as a spent one.
          exhausted: home.used && away.used,
          status: fixture.status,
          homeScore: fixture.homeScore,
          awayScore: fixture.awayScore,
        };
      });
  }, [fixtures, teams, search]);

  const availableCount = useMemo(
    () => board.reduce((n, row) => n + (row.home.used ? 0 : 1) + (row.away.used ? 0 : 1), 0),
    [board],
  );

  /** The flat list, kept for the Teams view: available first, then what you've spent. */
  const { available, used } = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = (teams ?? []).filter(
      (team) =>
        !query ||
        team.name.toLowerCase().includes(query) ||
        team.opponentName.toLowerCase().includes(query),
    );
    return {
      available: list.filter((t) => !t.used),
      used: list.filter((t) => t.used),
    };
  }, [teams, search]);

  if (notEntered) {
    return (
      <EmptyState
        icon={<Icon name="ticket" className="size-7" />}
        title="You haven't entered"
        description="Enter the competition to start picking teams."
      />
    );
  }

  if (eliminated) {
    return (
      <EmptyState
        icon={<Icon name="skull" className="size-7" />}
        title="You're out"
        description="Your run ended, but you can still watch the rest play it out on the Players and Grid tabs."
      />
    );
  }

  if (isLoading || fixturesLoading) {
    return (
      <div className="space-y-2.5">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-[4.5rem]" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {myPick?.team && (
        <div className="animate-pop flex items-center gap-4 rounded-card border border-accent-line bg-accent-soft p-4">
          <Crest url={myPick.team.crestUrl} name={myPick.team.name ?? ""} size={44} />
          <div className="min-w-0 flex-1">
            <p className="label text-accent-text">Your pick this round</p>
            <p className="truncate text-xl font-bold text-fg">{myPick.team.name}</p>
          </div>
          {picksOpen && (
            <span className="hidden shrink-0 text-[13px] text-fg-subtle sm:block">
              Choose another to change
            </span>
          )}
        </div>
      )}

      {!picksOpen && (
        <InfoNote icon="shield">
          Picks are locked for this round.
          {!myPick?.team && " You didn't get one in before the deadline."}
        </InfoNote>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      <section>
        <div className="mb-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <h2 className="text-sm font-semibold text-fg">
            This round
            <span className="ml-2 font-normal text-fg-subtle">
              {board.length} {board.length === 1 ? "match" : "matches"} ·{" "}
              {availableCount} {availableCount === 1 ? "team" : "teams"} left to you
            </span>
          </h2>

          {/* Stacked on a phone: side by side, the search field was squeezed to about ten
              characters by the toggle's labels. Both go full width instead, which also makes
              them comfortable thumb targets. */}
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {(teams?.length ?? 0) > 8 && (
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search teams…"
                icon="search"
                aria-label="Search teams"
                className="h-10 w-full sm:w-56"
              />
            )}
            <SegmentedControl
              size="sm"
              value={view}
              onChange={setView}
              options={PICK_VIEW_OPTIONS}
              className="w-full shrink-0 [&>button]:flex-1 sm:w-auto sm:[&>button]:flex-none"
            />
          </div>
        </div>

        {view === "fixtures" ? (
          !board.length ? (
            <EmptyState
              title={search ? "No matches found" : "No fixtures in this round"}
              description={
                search
                  ? "Try a different team name."
                  : "Fixtures haven't been imported for this round yet."
              }
            />
          ) : (
            <div className="stagger space-y-2.5">
              {board.map((row) => (
                <FixtureRowCard
                  key={row.fixtureId}
                  row={row}
                  selectedTeamId={myPick?.team?.id ?? null}
                  picksOpen={picksOpen}
                  pendingTeamId={mutation.isPending ? (mutation.variables as string) : null}
                  teamPlaysTwiceRule={rules.picks.teamPlaysTwice}
                  onSelect={(teamId) => mutation.mutate(teamId)}
                />
              ))}
            </div>
          )
        ) : (
          <div className="space-y-6">
            {!available.length ? (
              <EmptyState
                title={search ? "No teams match" : "No teams left to pick"}
                description={
                  search ? "Try a different search." : "You've used everyone playing this round."
                }
              />
            ) : (
              <div className="stagger grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {available.map((team) => (
                  <TeamButton
                    key={team.teamId}
                    team={team}
                    selected={myPick?.team?.id === team.teamId}
                    disabled={!picksOpen || mutation.isPending}
                    pending={mutation.isPending && mutation.variables === team.teamId}
                    teamPlaysTwiceRule={rules.picks.teamPlaysTwice}
                    onSelect={() => mutation.mutate(team.teamId)}
                  />
                ))}
              </div>
            )}

            {used.length > 0 && (
              <div>
                <div className="mb-3.5 flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold text-fg">Already used</h3>
                  <p className="text-[13px] text-fg-subtle">
                    {rules.picks.teamReuse === "reset_on_exhaustion"
                      ? `${used.length} used in this pool`
                      : `${used.length} unavailable`}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {used.map((team) => (
                    <TeamButton
                      key={team.teamId}
                      team={team}
                      disabled
                      teamPlaysTwiceRule={rules.picks.teamPlaysTwice}
                      onSelect={() => {}}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {availableCount === 0 && board.length > 0 && (
          <div className="mt-4">
            <InfoNote icon="shield">
              You've used every team playing this round.
              {rules.picks.teamReuse === "reset_on_exhaustion"
                ? " Your pool resets once you've been through them all."
                : " There's nothing left for you to pick here."}
            </InfoNote>
          </div>
        )}
      </section>
    </div>
  );
}

/* Fixture board ------------------------------------------------------------ */

interface FixtureSide {
  teamId: string;
  name: string;
  shortName: string;
  crestUrl: string | null;
  /** Already burned in this competition, so it can be shown but not chosen. */
  used: boolean;
  playsTwice: boolean;
  /** False only if the team isn't in the selectable set at all — treated as unpickable. */
  selectable: boolean;
}

interface FixtureRow {
  fixtureId: string;
  kickoffAt: string;
  home: FixtureSide;
  away: FixtureSide;
  exhausted: boolean;
  status: string;
  homeScore: number | null;
  awayScore: number | null;
}

/**
 * What the centre column of a fixture row says.
 *
 * A round runs for days, and the score is the thing that decides whether anyone is still in it,
 * so it takes the slot the kick-off time occupies beforehand. Once a match is over, the time it
 * started is the least interesting fact about it.
 */
function fixtureState(row: FixtureRow): { score: string | null; label: string | null } {
  const hasScore = row.homeScore != null && row.awayScore != null;
  const score = hasScore ? `${row.homeScore}–${row.awayScore}` : null;

  switch (row.status) {
    case "finished":
      return { score, label: "FT" };
    case "in_play":
      return { score, label: "Live" };
    case "postponed":
      return { score: null, label: "Postponed" };
    case "abandoned":
      return { score: null, label: "Abandoned" };
    case "cancelled":
      return { score: null, label: "Cancelled" };
    default:
      return { score: null, label: null };
  }
}

/**
 * One match, both sides pickable.
 *
 * A used team stays on the board rather than disappearing: knowing that Arsenal are playing and
 * that you've already spent them is the information the decision actually turns on. Hiding them
 * would leave a fixture list with holes in it and no explanation for the gaps.
 */
function FixtureRowCard({
  row,
  selectedTeamId,
  picksOpen,
  pendingTeamId,
  teamPlaysTwiceRule,
  onSelect,
}: {
  row: FixtureRow;
  selectedTeamId: string | null;
  picksOpen: boolean;
  pendingTeamId: string | null;
  teamPlaysTwiceRule: CompetitionRules["picks"]["teamPlaysTwice"];
  onSelect: (teamId: string) => void;
}) {
  const holdsSelection = selectedTeamId === row.home.teamId || selectedTeamId === row.away.teamId;
  const state = fixtureState(row);

  return (
    <div
      className={cx(
        "rounded-card border transition-colors duration-200",
        // The row shows *that* it holds your pick via its border; the fill belongs only to the
        // side you actually chose. Tinting the whole row made the opponent look picked too.
        holdsSelection
          ? "border-accent-line bg-surface"
          : row.exhausted
            ? "border-line/60 bg-transparent"
            : "border-line bg-surface",
      )}
    >
      <div className="flex items-stretch">
        <SideButton
          side={row.home}
          align="left"
          isSelected={selectedTeamId === row.home.teamId}
          picksOpen={picksOpen}
          isPending={pendingTeamId === row.home.teamId}
          teamPlaysTwiceRule={teamPlaysTwiceRule}
          opponentName={row.away.name}
          onSelect={onSelect}
        />

        <div className="flex shrink-0 flex-col items-center justify-center px-1.5 py-3 sm:px-3">
          {state.score ? (
            <span className="tabular whitespace-nowrap font-display text-[15px] font-bold text-fg">
              {state.score}
            </span>
          ) : (
            <span
              aria-hidden="true"
              className="font-display text-[13px] font-bold uppercase tracking-[0.1em] text-fg-faint"
            >
              v
            </span>
          )}
          <span
            className={cx(
              "tabular mt-1 whitespace-nowrap text-[11px]",
              row.status === "in_play" ? "font-semibold text-accent-text" : "text-fg-faint",
            )}
          >
            {state.label ?? formatTime(row.kickoffAt)}
          </span>
        </div>

        <SideButton
          side={row.away}
          align="right"
          isSelected={selectedTeamId === row.away.teamId}
          picksOpen={picksOpen}
          isPending={pendingTeamId === row.away.teamId}
          teamPlaysTwiceRule={teamPlaysTwiceRule}
          opponentName={row.home.name}
          onSelect={onSelect}
        />
      </div>

      {/* The centre column carries the time until a score displaces it; on a phone the day is
          what's missing, and once the match is under way the kick-off time comes back here. */}
      <p className="border-t border-line/60 px-3 py-1.5 text-[11px] text-fg-faint sm:hidden">
        {formatMatchDay(row.kickoffAt)}
        {state.label && ` · ${formatTime(row.kickoffAt)}`}
      </p>
    </div>
  );
}

function SideButton({
  side,
  align,
  isSelected,
  picksOpen,
  isPending,
  teamPlaysTwiceRule,
  opponentName,
  onSelect,
}: {
  side: FixtureSide;
  align: "left" | "right";
  isSelected: boolean;
  picksOpen: boolean;
  isPending: boolean;
  teamPlaysTwiceRule: CompetitionRules["picks"]["teamPlaysTwice"];
  opponentName: string;
  onSelect: (teamId: string) => void;
}) {
  const blocked = side.used || !side.selectable;
  const disabled = blocked || !picksOpen || isPending;

  /**
   * Disabled controls are skipped by most screen readers, so the reason has to live somewhere a
   * reader still reaches — hence the description below, and a label that says what the control
   * would do rather than just naming the team.
   */
  const label = blocked
    ? `${side.name} — already used, can't be picked`
    : `Pick ${side.name}, ${align === "left" ? "home" : "away"} against ${opponentName}`;

  return (
    <button
      type="button"
      onClick={() => onSelect(side.teamId)}
      disabled={disabled}
      aria-pressed={isSelected}
      aria-label={label}
      className={cx(
        "group flex min-h-[4.25rem] min-w-0 flex-1 items-center gap-2.5 p-3 transition-all duration-200 ease-out-quart sm:gap-3 sm:p-3.5",
        align === "right" && "flex-row-reverse text-right",
        align === "left" ? "rounded-l-card" : "rounded-r-card",
        blocked && "cursor-not-allowed opacity-60",
        !blocked && !picksOpen && "cursor-not-allowed opacity-60",
        !disabled && !isSelected && "hover:bg-tint active:bg-tint-strong",
        isSelected && "bg-accent-soft",
      )}
    >
      <span className="relative shrink-0">
        <Crest url={side.crestUrl} name={side.name} size={34} />
        {blocked && (
          // A struck-through crest reads as "spent" at a glance, before any label is parsed.
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 h-px w-full -translate-x-1/2 -translate-y-1/2 -rotate-12 bg-fg-subtle"
          />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cx(
            "flex items-center gap-1.5",
            align === "right" && "flex-row-reverse",
          )}
        >
          <span
            className={cx(
              "truncate text-[15px] font-semibold",
              isSelected ? "text-fg" : blocked ? "text-fg-subtle line-through" : "text-fg",
            )}
          >
            {/* Long club names blow out a two-column row on a phone. */}
            <span className="sm:hidden">{side.shortName}</span>
            <span className="hidden sm:inline">{side.name}</span>
          </span>

          {side.playsTwice && (
            <span
              title={
                teamPlaysTwiceRule === "must_win_both"
                  ? "Plays twice this round — must win both fixtures"
                  : "Plays twice this round — the first fixture counts"
              }
              className="grid size-[18px] shrink-0 place-items-center rounded-full bg-warn-soft text-[10px] font-bold text-warn-text"
            >
              2×
            </span>
          )}
        </span>

        <span
          className={cx(
            "mt-0.5 block truncate whitespace-nowrap text-[12px]",
            isSelected ? "text-accent-text" : "text-fg-faint",
          )}
        >
          {isSelected
            ? "Your pick"
            : blocked
              ? "Used"
              : align === "left"
                ? "Home"
                : "Away"}
        </span>
      </span>

      {isPending ? (
        <span className="grid size-6 shrink-0 place-items-center text-accent-text">
          <Icon name="refresh" className="size-4 animate-spin" />
        </span>
      ) : isSelected ? (
        <span className="hidden size-6 shrink-0 place-items-center rounded-full bg-accent text-accent-fg sm:grid">
          <Icon name="check" className="size-3.5" strokeWidth={2.6} label="Selected" />
        </span>
      ) : null}
    </button>
  );
}

function TeamButton({
  team,
  selected,
  disabled,
  pending,
  teamPlaysTwiceRule,
  onSelect,
}: {
  team: SelectableTeam;
  selected?: boolean;
  disabled?: boolean;
  pending?: boolean;
  teamPlaysTwiceRule: CompetitionRules["picks"]["teamPlaysTwice"];
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cx(
        "group relative flex min-h-[5.5rem] items-center gap-3.5 rounded-card border p-3.5 text-left",
        "transition-all duration-200 ease-out-quart",
        team.used && "cursor-not-allowed border-line bg-tint opacity-50",
        !team.used && !selected && "border-line bg-surface hover:border-line-strong",
        !team.used && !selected && !disabled && "hover:-translate-y-0.5 hover:shadow-lift",
        selected && "border-accent-line bg-accent-soft shadow-card",
        disabled && !team.used && "cursor-not-allowed opacity-60",
      )}
    >
      <Crest url={team.crestUrl} name={team.name} size={40} />

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-semibold text-fg">{team.name}</span>
          {team.playsTwice && (
            <span
              title={
                teamPlaysTwiceRule === "must_win_both"
                  ? "Plays twice this round — must win both fixtures"
                  : "Plays twice this round — the first fixture counts"
              }
              className="grid size-5 shrink-0 place-items-center rounded-full bg-warn-soft text-[10px] font-bold text-warn-text"
            >
              2×
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-[13px] text-fg-subtle">
          {team.isHome ? "vs" : "at"} {team.opponentName}
        </span>
        <span className="mt-0.5 block truncate text-[12px] text-fg-faint">
          {formatKickoff(team.kickoffAt)}
        </span>
      </span>

      {pending ? (
        <span className="grid size-6 shrink-0 place-items-center text-accent-text">
          <Icon name="refresh" className="size-4 animate-spin" />
        </span>
      ) : selected ? (
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent text-accent-fg">
          <Icon name="check" className="size-3.5" strokeWidth={2.6} />
        </span>
      ) : team.used ? (
        <Badge tone="neutral">Used</Badge>
      ) : null}
    </button>
  );
}

/* Players ------------------------------------------------------------------ */

/**
 * Everyone in the competition, with their full pick history. Deliberately open to every player
 * rather than admins only — knowing who's burned Arsenal already is part of the game, and it's
 * the same information the Grid tab shows, just per-person.
 *
 * Picks that haven't been revealed yet stay masked here exactly as they do everywhere else.
 */
function PlayersPanel({ competitionId }: { competitionId: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["leaderboard", competitionId],
    queryFn: () => api.competitions.leaderboard(competitionId),
  });

  const { data: grid } = useQuery({
    queryKey: ["results-grid", competitionId],
    queryFn: () => api.competitions.resultsGrid(competitionId),
  });

  if (isLoading) return <Skeleton className="h-80" />;
  if (!rows?.length) return <EmptyState title="Nobody has entered yet" />;

  return (
    <div className="stagger space-y-2.5">
      {rows.map((row, index) => (
        <PlayerRow
          key={row.entryId}
          row={row}
          rank={index + 1}
          grid={grid}
          expanded={expanded === row.entryId}
          onToggle={() => setExpanded(expanded === row.entryId ? null : row.entryId)}
        />
      ))}
    </div>
  );
}

function PlayerRow({
  row,
  rank,
  grid,
  expanded,
  onToggle,
}: {
  row: LeaderboardRow;
  rank: number;
  grid: ResultsGrid | undefined;
  expanded: boolean;
  onToggle: () => void;
}) {
  const history = useMemo(() => {
    if (!grid) return [];
    const byRound = new Map(
      grid.cells.filter((cell) => cell.entryId === row.entryId).map((cell) => [cell.roundId, cell]),
    );
    return grid.rounds
      .map((round) => ({ round, cell: byRound.get(round.id) }))
      .filter(({ cell }) => cell?.hasPicked);
  }, [grid, row.entryId]);

  return (
    <div className={cx("card overflow-hidden", row.status === "out" && "opacity-70")}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-tint"
      >
        <span className="tabular w-6 shrink-0 text-center text-sm font-bold text-fg-faint">
          {rank}
        </span>
        <Avatar
          name={row.displayName}
          color={row.avatarColor}
          size={40}
          dimmed={row.status === "out"}
        />

        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-fg">{row.displayName}</span>
          <span className="mt-0.5 block text-[12px] text-fg-subtle">
            {row.teamsUsed} {row.teamsUsed === 1 ? "team" : "teams"} used
            {row.status === "out" && row.eliminatedRound && ` · out in R${row.eliminatedRound}`}
          </span>
        </span>

        {row.status === "alive" ? (
          <Badge tone="alive">
            Alive{row.livesRemaining > 0 && ` · ${row.livesRemaining}`}
          </Badge>
        ) : (
          <Badge tone="out">Out</Badge>
        )}

        <Icon
          name="chevronDown"
          className={cx(
            "size-4 shrink-0 text-fg-faint transition-transform duration-300",
            expanded && "rotate-180",
          )}
        />
      </button>

      {expanded && (
        <div className="animate-fade border-t border-line p-4">
          {!history.length ? (
            <p className="text-[13px] text-fg-subtle">No picks yet.</p>
          ) : (
            <>
              <p className="label mb-2.5">Pick history</p>
              <div className="flex flex-wrap gap-2">
                {history.map(({ round, cell }) => (
                  <span
                    key={round.id}
                    className={cx(
                      "flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-[13px]",
                      cell?.outcome === "win" && "border-accent-line bg-accent-soft",
                      cell?.outcome === "loss" && "border-danger-line bg-danger-soft",
                      cell?.outcome === "void" && "border-warn-line bg-warn-soft",
                      (!cell?.outcome || cell.outcome === "pending") && "border-line bg-tint",
                    )}
                    title={
                      cell?.outcome === "void"
                        ? "Fixture voided — survived, and the team went back in the pool"
                        : undefined
                    }
                  >
                    <span className="tabular text-[11px] font-bold text-fg-faint">
                      R{round.roundNumber}
                    </span>
                    {cell?.team ? (
                      <>
                        <Crest url={cell.team.crestUrl} name={cell.team.name} size={16} />
                        <span className="font-medium text-fg">{cell.team.shortName}</span>
                      </>
                    ) : (
                      <span className="text-fg-faint">Hidden</span>
                    )}
                    {cell?.outcome === "win" && (
                      <Icon name="check" className="size-3.5 text-accent-text" strokeWidth={2.6} />
                    )}
                    {cell?.outcome === "loss" && (
                      <Icon name="close" className="size-3.5 text-danger-text" strokeWidth={2.6} />
                    )}
                    {cell?.outcome === "void" && (
                      <Icon name="minus" className="size-3.5 text-warn-text" strokeWidth={2.6} />
                    )}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* Rounds ------------------------------------------------------------------- */

function RoundsPanel({ competitionId }: { competitionId: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: rounds, isLoading } = useQuery({
    queryKey: ["rounds", competitionId],
    queryFn: () => api.competitions.rounds(competitionId),
  });

  if (isLoading) return <Skeleton className="h-80" />;
  if (!rounds?.length) return <EmptyState title="No rounds yet" />;

  return (
    <div className="stagger space-y-2.5">
      {rounds.map((round) => (
        <div key={round.id} className="card overflow-hidden">
          <button
            type="button"
            onClick={() => setExpanded(expanded === round.id ? null : round.id)}
            className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-tint"
            aria-expanded={expanded === round.id}
          >
            <span className="tabular grid size-11 shrink-0 place-items-center rounded-xl bg-tint-strong font-display text-xl font-bold text-fg">
              {round.roundNumber}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-fg">
                {formatKickoff(round.windowStart)}
              </span>
              <span className="block text-[13px] text-fg-subtle">
                {round.fixtureCount} fixtures
              </span>
            </span>
            <Badge tone={roundTone(round.status)}>{round.status}</Badge>
            <Icon
              name="chevronDown"
              className={cx(
                "size-4 shrink-0 text-fg-faint transition-transform duration-300",
                expanded === round.id && "rotate-180",
              )}
            />
          </button>

          {expanded === round.id && (
            <RoundDetail competitionId={competitionId} roundId={round.id} />
          )}
        </div>
      ))}
    </div>
  );
}

function roundTone(status: Round["status"]): "alive" | "warn" | "neutral" | "info" {
  if (status === "open") return "alive";
  if (status === "locked") return "warn";
  if (status === "settled") return "neutral";
  return "info";
}

function RoundDetail({ competitionId, roundId }: { competitionId: string; roundId: string }) {
  const { data: picks, isLoading } = useQuery({
    queryKey: ["picks", competitionId, roundId],
    queryFn: () => api.competitions.picks(competitionId, roundId),
  });

  if (isLoading) {
    return (
      <div className="animate-fade border-t border-line p-4">
        <Skeleton className="h-20" />
      </div>
    );
  }
  if (!picks?.length) return null;

  return (
    <div className="animate-fade border-t border-line p-4">
      <p className="label mb-3">Picks</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {picks.map((pick) => (
          <div
            key={pick.entryId}
            className={cx(
              "flex items-start gap-2.5 rounded-xl border px-3 py-2.5",
              pick.outcome === "win" && "border-accent-line bg-accent-soft",
              pick.outcome === "loss" && "border-danger-line bg-danger-soft",
              pick.outcome === "void" && "border-warn-line bg-warn-soft",
              (!pick.outcome || pick.outcome === "pending") && "border-line bg-tint",
            )}
          >
            <Avatar name={pick.displayName} color={pick.avatarColor} size={26} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium text-fg">
                {pick.displayName}
              </span>
              <span className="block truncate text-[12px] text-fg-subtle">
                {pick.team?.name ?? (pick.hasPicked ? "Hidden until deadline" : "No pick")}
              </span>
              {/* Why this player survived or went out, straight from settlement. Nobody should
                  have to ask an admin what happened to them. */}
              {pick.settlementReason && (
                <span className="mt-0.5 block text-[12px] leading-snug text-fg-faint">
                  {pick.settlementReason}
                </span>
              )}
            </span>
            {pick.outcome === "win" && (
              <Icon name="check" className="size-4 text-accent-text" strokeWidth={2.4} />
            )}
            {pick.outcome === "loss" && (
              <Icon name="close" className="size-4 text-danger-text" strokeWidth={2.4} />
            )}
            {pick.outcome === "void" && (
              <Icon
                name="minus"
                className="size-4 text-warn-text"
                strokeWidth={2.4}
                label="Fixture voided — survived"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Rules -------------------------------------------------------------------- */

function RulesPanel({ summary, locked }: { summary: string[]; locked: boolean }) {
  return (
    <div className="card p-6">
      <div className="mb-5 flex items-center justify-between gap-4">
        <h2 className="display-title text-2xl text-fg">Rules in force</h2>
        <Badge tone={locked ? "warn" : "info"}>{locked ? "Locked" : "Editable until R1"}</Badge>
      </div>

      <ul className="space-y-3">
        {summary.map((line) => (
          <li key={line} className="flex gap-3 text-sm leading-relaxed text-fg-muted">
            <Icon name="check" className="mt-0.5 size-4 shrink-0 text-accent-text" />
            {line}
          </li>
        ))}
      </ul>

      {locked && (
        <p className="mt-6 border-t border-line pt-5 text-[13px] leading-relaxed text-fg-subtle">
          These locked when round 1 started, so the rules you started under are the rules you
          finish under — nothing that decides who's eliminated can change now. The admin can
          still adjust when picks are revealed to everyone else.
        </p>
      )}
    </div>
  );
}


